// @flow
// lots of these in the reducer, and the rule is usually wrong anyway
/* eslint-disable no-param-reassign */

import type { ExtractReturn } from '@ta/common.types';
import type { State as PageState } from '../page';
import { SET_HOTELS_DATES_GUESTS } from '../travelerInfo/hotels';

const SET_GEO = 'SET_GEO';
export const setGeo = (geoId: number) => ({
  type: SET_GEO,
  geoId,
});

const SET_DETAIL_ID = 'SET_DETAIL_ID';
export const setDetailId = (detailId: number) => ({
  type: SET_DETAIL_ID,
  detailId,
});

const SET_CROSSSELLS_ENDPOINT_RESULTS = 'SET_CROSSSELLS_ENDPOINT_RESULTS';
export const setCrossSellsEndpointResults = (endpointResults: any) => ({
  type: SET_CROSSSELLS_ENDPOINT_RESULTS,
  endpointResults,
});

const CROSSSELLS_POLL = 'CROSSSELLS_POLL';
export const crossSellsPoll = () => ({
  type: CROSSSELLS_POLL,
});

const REGISTER_LOCATION = 'REGISTER_LOCATION';
export const registerLocation = (locationName: string) => ({
  type: REGISTER_LOCATION,
  locationName,
});

const SET_UI_ASSETS = 'SET_UI_ASSETS';
export const setUIAssets = (uiAssets: any) => ({
  type: SET_UI_ASSETS,
  uiAssets,
});

const REFRESH_PRICES = 'REFRESH_PRICES';
export const refreshPrices = () => ({
  type: REFRESH_PRICES,
});

const HR_TEMPLATE_TRANSITION = 'HR_TEMPLATE_TRANSITION';
export const templateTransition = (routeInfo: ?Object, transData: ?Object) => ({
  type: HR_TEMPLATE_TRANSITION,
  routeInfo,
  transData,
});

const SET_IS_META_WITH_VP = 'SET_IS_META_WITH_VP';
export const setIsMetaWithVp = (isMetaWithVp: boolean) => ({
  type: SET_IS_META_WITH_VP,
  isMetaWithVp,
});

export type Action =
  | ExtractReturn<typeof setGeo>
  | ExtractReturn<typeof crossSellsPoll>
  | ExtractReturn<typeof setDetailId>
  | ExtractReturn<typeof setCrossSellsEndpointResults>
  | ExtractReturn<typeof refreshPrices>
  | ExtractReturn<typeof registerLocation>
  | ExtractReturn<typeof setIsMetaWithVp>;

const initCrossSellState = () => ({
  requestNumber: 1,
  atLeastOneLocationRegistered: false,
  updatingResults: false,
});

export const createState = (
  geoId?: ?number | string,
  detailId?: ?number | string,
  oldState?: Object,
) => {
  const newGeo = geoId ? Number(geoId) : 0;
  const newDetail = detailId ? Number(detailId) : 0;
  if (
    oldState &&
    oldState.name === 'LOCATION_DETAIL' &&
    oldState.geoId === newGeo &&
    oldState.detailId === newDetail
  ) {
    // Navigate action to the same page -- happens when the hash changes, e.g., when we close the map.
    return oldState;
  }
  return {
    name: 'LOCATION_DETAIL',
    geoId: newGeo,
    detailId: newDetail,
    crossSells: initCrossSellState(),
    refreshCount: 0,
    isMetaWithVp: false,
  };
};

const buildNewState = (oldState: PageState, updateNewState: PageState => void) => {
  const newState = { ...oldState };
  if (!newState.crossSells) {
    newState.crossSells = initCrossSellState();
  }
  newState.crossSells.updatingResults = false;
  updateNewState(newState);
  return newState;
};

export type State = ExtractReturn<typeof createState>;

export default (state: PageState = createState(), action: Action): PageState => {
  if (state.name !== 'LOCATION_DETAIL') {
    return state;
  }

  switch (action.type) {
    case CROSSSELLS_POLL:
      return buildNewState(state, newState => {
        newState.crossSells.requestNumber = (state.crossSells.requestNumber || 0) + 1;
      });
    case SET_CROSSSELLS_ENDPOINT_RESULTS:
      return buildNewState(state, newState => {
        newState.crossSells.endpointResults = action.endpointResults;
        newState.crossSells.updatingResults = true;
      });
    case SET_UI_ASSETS:
      return buildNewState(state, newState => {
        newState.crossSells.uiAssets = action.uiAssets;
      });
    case SET_HOTELS_DATES_GUESTS:
      return buildNewState(state, newState => {
        newState.crossSells.requestNumber = 1;

        // Force Meta NOT to include VP offers with new travel info
        newState.isMetaWithVp = false;
      });
    case SET_GEO:
      return buildNewState(state, newState => {
        newState.crossSells.requestNumber = 1;
        newState.geoId = action.geoId;
      });
    case SET_DETAIL_ID:
      return buildNewState(state, newState => {
        newState.detailId = action.detailId;
        newState.crossSells.requestNumber = 1;
      });
    case REGISTER_LOCATION:
      return buildNewState(state, newState => {
        newState.crossSells.atLeastOneLocationRegistered = true;
      });
    case REFRESH_PRICES:
      return buildNewState(state, newState => {
        newState.refreshCount = (state.refreshCount || 0) + 1;
      });
    case HR_TEMPLATE_TRANSITION:
      if (action.transData) {
        return buildNewState(state, newState => {
          newState.transData = action.transData;
          newState.transRoute = action.routeInfo;
        });
      }
      return state;
    case SET_IS_META_WITH_VP: {
      const { isMetaWithVp } = action;
      return state.isMetaWithVp === isMetaWithVp ? state : { ...state, isMetaWithVp };
    }
    default:
      return state;
  }
};
